import React from "react";
import { Pressable, StyleSheet, useWindowDimensions, View } from "react-native";
import Modal from "react-native-modal";
import { Text } from "react-native-paper";
import { useToggle } from "react-use";
import { CreateResponsiveStyle, DEVICE_SIZES, maxSize } from "rn-responsive-styles";
import AsideLeft from "../home/aside-left/aside-left";
import { SideMenuContext } from "./side-menu";
import { useSelector } from "react-redux";
import { getUserProfileSelector } from "app/modules/account-management/reducer/selectors";
import { POST_STATUS } from "app/modules/home/home";

interface SideMenuProviderProps {
  children?: JSX.Element | JSX.Element[];
}

export function SideMenuProvider(props: SideMenuProviderProps) {
  const [isOpen, toggle] = useToggle(false);

  const openMenu = React.useCallback(() => {
    toggle(true);
  }, [toggle]);

  const hideMenu = React.useCallback(() => {
    toggle(false);
  }, [toggle]);

  const { width, height } = useWindowDimensions();
  const LeftMenu = <AsideLeft />;
  const { styles, deviceSize } = useStyles();

  React.useEffect(() => {
    if (deviceSize >= DEVICE_SIZES.LARGE_DEVICE) {
      hideMenu();
    }
  }, [deviceSize, hideMenu]);

  const styleBackdrop: any = {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
  };

  const userProfile = useSelector(getUserProfileSelector);

  const customBackdrop = <View style={styleBackdrop} />;

  const stylesMemo = React.useMemo(
    () =>
      StyleSheet.create({
        menuView: {
          display: userProfile?.POSTSTATUS === POST_STATUS.FORCE || userProfile?.POSTSTATUS === POST_STATUS.LOCK ? "none" : "flex",
        },
      }),
    [userProfile?.POSTSTATUS],
  );

  return (
    <SideMenuContext.Provider
      value={{
        isOpen,
        openMenu,
        hideMenu,
      }}>
      <View style={[styles("container"), stylesMemo.menuView]}>
        <View style={styles("drawerMenuStyles")}>{LeftMenu}</View>
        <View style={styles("content")}>{props.children}</View>
      </View>
      <Modal
        style={styles("modalContainer")}
        swipeDirection="left"
        deviceHeight={height}
        onSwipeComplete={hideMenu}
        deviceWidth={width}
        customBackdrop={customBackdrop}
        animationIn="slideInLeft"
        animationOut="slideOutLeft"
        onDismiss={hideMenu}
        onBackdropPress={hideMenu}
        isVisible={isOpen}>
        <View style={styles("flexRow")}>
          <View style={styles("drawerWidth")}>{LeftMenu}</View>
          <Pressable style={styles("closeButton")} onPress={hideMenu}>
            <Text children={"X"} style={styles("closeIcon")} />
          </Pressable>
        </View>
      </Modal>
    </SideMenuContext.Provider>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      flexDirection: "row",
    },
    modalContainer: {
      margin: 0,
      flex: 1,
    },
    flexRow: {
      flexDirection: "row",
      flex: 1,
    },
    content: { flex: 1 },
    closeButton: { backgroundColor: "white", height: 40, width: 40, justifyContent: "center", alignItems: "center" },
    closeIcon: { fontSize: 24 },
    drawerWidth: {
      height: "100%",
      width: 300,
    },
    drawerMenuStyles: { display: "flex", width: 300 },
  },
  {
    [DEVICE_SIZES.EXTRA_LARGE_DEVICE]: {
      drawerMenuStyles: { display: "flex", width: 300 },
    },
    [maxSize(DEVICE_SIZES.MEDIUM_DEVICE)]: {
      drawerMenuStyles: { display: "none", width: 300 },
    },
    [DEVICE_SIZES.EXTRA_SMALL_DEVICE]: {
      drawerMenuStyles: { display: "none", width: 300 },
    },
  },
);
